import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "@store/baseQueries";
import {
  createUser,
  editUser,
  fetchCalenderMonths,
  fetchCompanies,
  fetchCountries,
  fetchImportAndExportData,
  fetchProducts,
  fetchSectionMonths,
  fetchSectionProducts,
  fetchSubtypes,
  fetchUsers,
  fetchYears,
  updateImportAndExportData,
} from "./queries";
import { mastersBaseUrl } from "@store/urlPaths";
import { message } from "antd";
import { getErrorMessage } from "@utils/error";

const mastersApi = createApi({
  reducerPath: "masters",
  baseQuery: baseQuery(mastersBaseUrl),
  tagTypes: ["USERS","IMPORTS_AND_EXPORTS"],
  endpoints: (builder) => ({
    // fetch products
    fetchProducts: builder.query({
      query: fetchProducts,
      transformResponse: (res) => {
        res.data.sort((a, b) => a.name.localeCompare(b.name));
        return res;
      },
    }),

    // fetch section products
    fetchSectionProducts: builder.query({
      query: fetchSectionProducts,
      transformResponse: (res) => {
        res.list.sort((a, b) => a.localeCompare(b));
        return res;
      },
    }),

    // fetch section months
    fetchSectionMonths: builder.query({
      query: fetchSectionMonths,
    }),

    //fetch calender month
    fetchCalenderMonths: builder.query({
      query: fetchCalenderMonths,
    }),

    //fetch companies
    fetchCompanies: builder.query({
      query: fetchCompanies,
    }),

    //fetch countries
    fetchCountries: builder.query({
      query: fetchCountries,
    }),

    //fetch years
    fetchYears: builder.query({
      query: fetchYears,
    }),

    //fetch users
    fetchUsers: builder.query({
      query: fetchUsers,
      providesTags: ["USERS"],
    }),

    // create user
    createUser: builder.mutation({
      query: createUser,
      invalidatesTags: ["USERS"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.success) {
            message.success(data.message);
          }
        } catch (error) {
          message.error(getErrorMessage(error));
        }
      },
    }),

    // edit user
    editUser: builder.mutation({
      query: editUser,
      invalidatesTags: ["USERS"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.success) {
            message.success(data.message);
          }
        } catch (error) {
          message.error(getErrorMessage(error));
        }
      },
    }),

    //fetch substypes
    fetchSubtypes: builder.query({
      query: fetchSubtypes,
    }),

    //fetch imports and exports
    fetchImportsAndExports: builder.query({
      query: fetchImportAndExportData,
      providesTags: ["IMPORTS_AND_EXPORTS"],
    }),

    //update imports and exports
    updateImportsAndExports: builder.mutation({
      query: updateImportAndExportData,
      invalidatesTags: ["IMPORTS_AND_EXPORTS"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.success) {
            message.success(data.message);
          }
        } catch (error) {
          message.error(getErrorMessage(error));
        }
      },
    }),
  }),
});

export const {
  useFetchProductsQuery,
  useFetchSectionProductsQuery,
  useFetchSectionMonthsQuery,
  useFetchCalenderMonthsQuery,
  useFetchCompaniesQuery,
  useFetchCountriesQuery,
  useFetchYearsQuery,
  useFetchUsersQuery,
  useCreateUserMutation,
  useEditUserMutation,
  useFetchSubtypesQuery,
  useLazyFetchImportsAndExportsQuery,
  useUpdateImportsAndExportsMutation,
  reducer: mastersReducer,
  reducerPath: mastersReducerPath,
  middleware: mastersMiddleware,
} = mastersApi;
